import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Robot, PaperPlaneRight, Check, X, SpinnerGap } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AITournamentAssistant({ tournamentId, onGroupsUpdated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/tournament-assistant`, {
        message: userMsg,
        tournament_id: tournamentId,
      }, { withCredentials: true });

      const { response, action } = res.data;

      // Extract the human-readable part (before the JSON block)
      let displayText = response;
      if (response.includes("```json")) {
        displayText = response.split("```json")[0].trim();
        if (!displayText) displayText = action?.explanation || "Here's what I suggest:";
      }

      setMessages(prev => [...prev, { role: "ai", text: displayText, action }]);
      if (action && action.action !== "clarify") {
        setPendingAction(action);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", text: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const applyAction = async () => {
    if (!pendingAction || !tournamentId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/apply-action`, {
        action: pendingAction,
        tournament_id: tournamentId,
      }, { withCredentials: true });
      toast.success(res.data.message);
      setMessages(prev => [...prev, { role: "system", text: `Applied: ${res.data.message}` }]);
      setPendingAction(null);
      if (onGroupsUpdated) onGroupsUpdated(res.data.groups);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply action");
    } finally {
      setLoading(false);
    }
  };

  const dismissAction = () => {
    setPendingAction(null);
    setMessages(prev => [...prev, { role: "system", text: "Action dismissed." }]);
  };

  return (
    <Card className="bg-card border-border" data-testid="ai-tournament-assistant">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Robot className="w-5 h-5 text-primary" weight="fill" />
          AI Tournament Assistant
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Tell me what you need, e.g. "Create 7 groups of 4 balanced by skill" or "Move player X to Group B"
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Chat messages */}
        <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="ai-chat-messages">
          {messages.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <Robot className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Ask me anything about tournament setup!</p>
              <div className="flex flex-wrap gap-1.5 justify-center mt-3">
                {["Create 7 groups of 4 players", "Balance groups by skill", "Move Ahmed to Group A"].map(s => (
                  <button key={s} onClick={() => setInput(s)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={`msg-${i}-${msg.role}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : msg.role === "system"
                  ? "bg-muted/50 text-muted-foreground italic text-xs"
                  : "bg-muted/30 border border-border"
              }`}>
                <p className="whitespace-pre-wrap">{msg.text}</p>
                {msg.action && msg.action.action !== "clarify" && (
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">Suggested Action</p>
                    {msg.action.groups && (
                      <div className="flex flex-wrap gap-1">
                        {msg.action.groups.map((g, gi) => (
                          <Badge key={g.group_name || `g-${gi}`} variant="outline" className="text-[10px]">
                            Group {g.group_name}: {g.player_ids?.length || 0} players
                          </Badge>
                        ))}
                      </div>
                    )}
                    {msg.action.explanation && (
                      <p className="text-[11px] text-muted-foreground mt-1">{msg.action.explanation}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-muted/30 border border-border rounded-lg px-3 py-2">
                <SpinnerGap className="w-4 h-4 animate-spin text-primary" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Pending action confirmation */}
        {pendingAction && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-primary/10 border border-primary/30" data-testid="ai-pending-action">
            <p className="flex-1 text-xs text-primary">
              Apply this action? ({pendingAction.groups?.length || 0} groups)
            </p>
            <Button size="sm" className="h-7 text-xs" onClick={applyAction} disabled={loading} data-testid="ai-apply-btn">
              <Check className="w-3 h-3 mr-1" />Apply
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={dismissAction} data-testid="ai-dismiss-btn">
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}

        {/* Input */}
        <form onSubmit={e => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="e.g. Create 7 groups of 4 balanced by ELO..."
            className="flex-1 bg-background border-border text-sm"
            disabled={loading}
            data-testid="ai-input"
          />
          <Button type="submit" size="sm" disabled={loading || !input.trim()} data-testid="ai-send-btn">
            <PaperPlaneRight className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
